import { DatePicker, Input, Select, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { RefreshIcon, TrashIcon } from '../../assets/icons';
import type { ExistingTask, TaskTouch } from '../../features/aiPlanner/plannerApi';
import TaskCards, { type CoordinatorOption } from './TaskCards';
import { type WsUI, PRIORITY_OPTS } from './planUi';

/* UI state for the change set: the add-part uses the shared task cards; the
 * touches carry a `_dropped` flag so the team can leave an item out. */
export type TouchUI = TaskTouch & { _dropped?: boolean };

export interface ChangeUI {
  add: WsUI[];
  modify: TouchUI[];
  remove: TouchUI[];
  flag: TouchUI[];
}

interface Props {
  value: ChangeUI;
  onChange: (next: ChangeUI) => void;
  existing: Map<number, ExistingTask>;
  coordinators: CoordinatorOption[];
  eventDate?: string | null;
  notes: string[];
}

const fmt = (d?: string | null) => (d ? dayjs(d).format('D MMM YYYY') : '—');

function Section({ title, count, tone, hint, children }: {
  title: string; count: number; tone: 'add' | 'modify' | 'remove' | 'flag'; hint: string; children: React.ReactNode;
}) {
  const color = { add: 'green', modify: 'blue', remove: 'red', flag: 'red' }[tone];
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Tag color={color} className="!m-0 !font-semibold">{title}</Tag>
        <span className="text-sm font-semibold text-ink">{count}</span>
        <span className="text-xs text-ink-secondary">{hint}</span>
      </div>
      {count === 0 ? <div className="text-xs text-ink-secondary">Nothing here.</div> : children}
    </section>
  );
}

function ExistingHead({ t, status }: { t?: ExistingTask; status?: string | null }) {
  if (!t) return <span className="text-xs text-ink-secondary">(task no longer exists)</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-secondary">
      <span className="font-medium text-ink">#{t.id}</span>
      <Tag className="!m-0" color={t.parent_id ? 'cyan' : 'blue'}>{t.parent_id ? 'SUB-TASK' : 'TASK'}</Tag>
      {status && <Tag className="!m-0">{status}</Tag>}
      {t.part && <span>· {t.part}</span>}
      {t.assignee && <span>· {t.assignee}</span>}
      {t.due_date && <span>· due {fmt(t.due_date)}</span>}
    </div>
  );
}

const DropBtn = ({ dropped, onClick, label }: { dropped?: boolean; onClick: () => void; label: string }) => (
  <Tooltip title={dropped ? 'Put it back' : label}>
    <button type="button" onClick={onClick}
      className={['flex size-7 shrink-0 items-center justify-center rounded-md transition-colors',
        dropped ? 'text-brand hover:bg-brand-shade' : 'text-[#9ca3af] hover:bg-[#FEF2F2] hover:text-[#DC2626]'].join(' ')}>
      {dropped ? <RefreshIcon className="size-4" /> : <TrashIcon className="size-4" />}
    </button>
  </Tooltip>
);

/** The proposed changes against the engagement's existing tasks: Add / Change / Remove / No longer needed. */
export default function ChangeSetView({ value, onChange, existing, coordinators, eventDate, notes }: Props) {
  const upd = <K extends 'modify' | 'remove' | 'flag'>(key: K, i: number, patch: Partial<TouchUI>, byUser = true) =>
    onChange({ ...value, [key]: value[key].map((x, j) => (j !== i ? x : { ...x, ...patch, ...(byUser ? { _byUser: true } : {}) })) });
  const live = (arr: TouchUI[]) => arr.filter((x) => !x._dropped).length;
  const nAdd = value.add.reduce((n, w) => n + w.tasks.filter((t) => !t._dropped && t.title.trim()).length, 0);

  return (
    <div className="flex flex-col gap-6">
      {notes.length > 0 && (
        <div className="rounded-lg border border-line-soft bg-bg/60 px-3 py-2 text-xs text-ink-secondary">
          <div className="mb-1 font-semibold text-ink">Notes from the AI</div>
          <ul className="ml-4 list-disc">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}

      <Section title="ADD" tone="add" count={nAdd} hint="new tasks and sub-tasks">
        <TaskCards value={value.add} onChange={(add) => onChange({ ...value, add })}
          coordinators={coordinators} eventDate={eventDate} />
      </Section>

      <Section title="CHANGE" tone="modify" count={live(value.modify)} hint="existing tasks that need an update">
        <div className="flex flex-col gap-2">
          {value.modify.map((m, i) => {
            const t = existing.get(m.task_id);
            return (
              <div key={m.task_id} className={['rounded-lg border bg-white p-3', m._dropped ? 'border-dashed opacity-50' : 'border-line-soft'].join(' ')}>
                <div className="flex items-start gap-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <ExistingHead t={t} status={t?.status} />
                    <div className="text-xs text-ink-secondary">Why: {m.reason}</div>
                    {/* before -> after */}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="rounded-md bg-bg/70 p-2 text-xs">
                        <div className="mb-1 font-semibold text-ink-secondary">Now</div>
                        <div className="font-medium text-ink">{t?.title}</div>
                        {t?.description && <div className="mt-1 text-ink-secondary">{t.description}</div>}
                        <div className="mt-1 text-ink-secondary">{t?.priority ?? '—'} · due {fmt(t?.due_date)} · {t?.assignee ?? 'unassigned'}</div>
                      </div>
                      <div className="flex flex-col gap-1.5 rounded-md border border-brand/30 bg-brand-shade/40 p-2 text-xs">
                        <div className="font-semibold text-brand">After</div>
                        <Input size="small" disabled={m._dropped} placeholder={t?.title ?? 'Title'} value={m.title ?? ''}
                          onChange={(e) => upd('modify', i, { title: e.target.value || null })} />
                        <Input.TextArea size="small" disabled={m._dropped} autoSize={{ minRows: 1, maxRows: 4 }}
                          placeholder={t?.description || 'Details (unchanged)'} value={m.description ?? ''}
                          onChange={(e) => upd('modify', i, { description: e.target.value || null })} />
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Select size="small" className="w-[100px]" disabled={m._dropped} allowClear placeholder={t?.priority ?? 'Priority'}
                            value={m.priority ?? undefined} options={PRIORITY_OPTS}
                            onChange={(v) => upd('modify', i, { priority: v ?? null })} />
                          <DatePicker size="small" disabled={m._dropped} placeholder={fmt(t?.due_date)}
                            value={m.due_date ? dayjs(m.due_date) : null}
                            onChange={(d) => upd('modify', i, { due_date: d ? d.format('YYYY-MM-DD') : null })} />
                          <Select size="small" className="min-w-[170px]" disabled={m._dropped} allowClear showSearch optionFilterProp="label"
                            placeholder={t?.assignee ?? 'Assignee (unchanged)'}
                            value={m.suggested_assignee_id ?? undefined} options={coordinators}
                            onChange={(v) => upd('modify', i, { suggested_assignee_id: v ?? null, assignee_reason: null })} />
                        </div>
                        {m.assignee_reason && <div className="text-ink-secondary">Why this person: {m.assignee_reason}</div>}
                      </div>
                    </div>
                  </div>
                  <DropBtn dropped={m._dropped} label="Keep this task as it is" onClick={() => upd('modify', i, { _dropped: !m._dropped }, false)} />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="REMOVE" tone="remove" count={live(value.remove)} hint="tasks that will be deleted (only planned / pending ones)">
        <div className="flex flex-col gap-2">
          {value.remove.map((r, i) => {
            const t = existing.get(r.task_id);
            return (
              <div key={r.task_id} className={['rounded-lg border bg-white p-3', r._dropped ? 'border-dashed opacity-50' : 'border-[#FECACA]'].join(' ')}>
                <div className="flex items-start gap-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <ExistingHead t={t} status={t?.status} />
                    <div className={['text-sm font-medium', r._dropped ? 'text-ink' : 'text-ink line-through decoration-[#DC2626]/60'].join(' ')}>{t?.title}</div>
                    <div className="text-xs text-ink-secondary">Why: {r.reason}</div>
                  </div>
                  <DropBtn dropped={r._dropped} label="Keep this task" onClick={() => upd('remove', i, { _dropped: !r._dropped }, false)} />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="NO LONGER NEEDED" tone="flag" count={value.flag.length}
        hint="already completed or in progress — never removed, only marked for you to decide">
        <div className="flex flex-col gap-2">
          {value.flag.map((f) => {
            const t = existing.get(f.task_id);
            return (
              <div key={f.task_id} className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-3">
                <div className="flex flex-col gap-1">
                  <ExistingHead t={t} status={t?.status} />
                  <div className="text-sm font-medium text-[#991B1B]">{t?.title}</div>
                  <div className="text-xs text-[#B91C1C]">Not required after this change: {f.reason}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
